import {
  PolicyList,
  PolicyPage,
  PolicyParagraph,
  PolicySection,
  PolicySubsection,
} from "./policy/PolicyPage";

/**
 * Privacy Policy. Built from the shared `PolicyPage` kit so it matches the
 * cookie and terms pages. Company is Artisaire; the product is the Artisaire
 * Guestbook Service; contact is info@artisaire.com.
 */
export function PrivacyPolicy() {
  return (
    <PolicyPage title="Privacy Policy" lastUpdated="June 12, 2026">
      <PolicySection title="Introduction">
        <PolicyParagraph>
          This Privacy Policy describes how Artisaire (&ldquo;Company,&rdquo;
          &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) collects,
          uses, discloses, and protects information when you use Artisaire
          Guestbook (the &ldquo;Service&rdquo;).
        </PolicyParagraph>
        <PolicyParagraph>
          By using the Service, you agree to the collection and use of
          information in accordance with this Privacy Policy.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Information We Collect">
        <PolicySubsection title="Information You Provide">
          <PolicyParagraph>
            We may collect information that you voluntarily provide, including:
          </PolicyParagraph>
          <PolicyList
            items={[
              "Name",
              "Email address",
              "Phone number",
              "Mailing address",
              "Account credentials",
              "Profile information",
              "Event information",
              "Contact records you create, upload, or manage through the Service",
              "Communications with us",
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="Contact Information Managed Through the Service">
          <PolicyParagraph>
            The Service allows users to create, store, organize, and share
            contact information for event planning purposes.
          </PolicyParagraph>
          <PolicyParagraph>Such information may include:</PolicyParagraph>
          <PolicyList
            items={[
              "Names",
              "Email addresses",
              "Phone numbers",
              "Mailing addresses",
              "Organization names",
              "Event-related notes",
              "Other information entered by users",
            ]}
          />
          <PolicyParagraph>
            You are responsible for ensuring that you have the legal right and
            appropriate permissions to upload, store, process, and share any
            information about third parties through the Service.
          </PolicyParagraph>
        </PolicySubsection>

        <PolicySubsection title="Automatically Collected Information">
          <PolicyParagraph>
            When you use the Service, we may automatically collect:
          </PolicyParagraph>
          <PolicyList
            items={[
              "IP address",
              "Browser type",
              "Device type",
              "Operating system",
              "Pages visited",
              "Usage patterns",
              "Session information",
              "Diagnostic and performance data",
            ]}
          />
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="How We Use Information">
        <PolicyParagraph>We may use information to:</PolicyParagraph>
        <PolicyList
          items={[
            "Provide and maintain the Service",
            "Create and manage user accounts",
            "Authenticate users",
            "Enable contact management features",
            "Enable sharing and exporting of contact information",
            "Process support requests",
            "Improve functionality and user experience",
            "Monitor usage and performance",
            "Detect fraud, abuse, or security incidents",
            "Communicate service updates and important notices",
            "Comply with legal obligations",
          ]}
        />
      </PolicySection>

      <PolicySection title="User-Controlled Sharing and Exports">
        <PolicyParagraph>
          A core function of the Service is enabling users to share or export
          contact information.
        </PolicyParagraph>
        <PolicyParagraph>
          When you choose to share, export, or transmit information through the
          Service, we process that information according to your instructions.
        </PolicyParagraph>
        <PolicyParagraph>
          We do not independently verify whether you have obtained consent from
          individuals whose information you upload or share. You are solely
          responsible for ensuring that your collection, use, and sharing of
          information complies with applicable laws.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Legal Bases for Processing">
        <PolicyParagraph>
          Where applicable, we process personal information based on:
        </PolicyParagraph>
        <PolicyList
          items={[
            "Your consent",
            "Performance of a contract",
            "Compliance with legal obligations",
            "Legitimate business interests",
          ]}
        />
      </PolicySection>

      <PolicySection title="Sharing of Information">
        <PolicyParagraph>We may share information:</PolicyParagraph>

        <PolicySubsection title="Service Providers">
          <PolicyParagraph>
            With vendors that assist us in operating the Service, including:
          </PolicyParagraph>
          <PolicyList
            items={[
              "Cloud hosting providers",
              "Analytics providers",
              "Customer support providers",
              "Email delivery providers",
              "Authentication providers",
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="Business Transfers">
          <PolicyParagraph>
            In connection with a merger, acquisition, financing, asset sale, or
            similar transaction.
          </PolicyParagraph>
        </PolicySubsection>

        <PolicySubsection title="Legal Requirements">
          <PolicyParagraph>
            When required by law or necessary to:
          </PolicyParagraph>
          <PolicyList
            items={[
              "Comply with legal obligations",
              "Protect rights and property",
              "Investigate security incidents",
              "Prevent fraud",
              "Protect users and the public",
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="With Your Instructions">
          <PolicyParagraph>
            When you choose to share, export, or disclose information using the
            Service.
          </PolicyParagraph>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="Data Retention">
        <PolicyParagraph>
          We retain personal information for as long as necessary to:
        </PolicyParagraph>
        <PolicyList
          items={[
            "Provide the Service",
            "Maintain business records",
            "Comply with legal obligations",
            "Resolve disputes",
            "Enforce agreements",
          ]}
        />
        <PolicyParagraph>
          Deleted information may remain in backups for a limited period before
          being permanently removed.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="International Transfers">
        <PolicyParagraph>
          Information may be processed and stored in countries other than your
          own. By using the Service, you consent to such transfers.
        </PolicyParagraph>
        <PolicyParagraph>
          We take reasonable measures to ensure appropriate safeguards are in
          place.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Your Rights">
        <PolicyParagraph>
          Depending on your jurisdiction, you may have the right to:
        </PolicyParagraph>
        <PolicyList
          items={[
            "Access your information",
            "Correct inaccurate information",
            "Request deletion",
            "Object to certain processing",
            "Withdraw consent",
            "Request portability of your information",
          ]}
        />
        <PolicyParagraph>
          Requests may be submitted to{" "}
          <a
            href="mailto:info@artisaire.com"
            className="text-brand underline-offset-4 hover:underline"
          >
            info@artisaire.com
          </a>
          .
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Security">
        <PolicyParagraph>
          We implement reasonable administrative, technical, and organizational
          safeguards to protect information.
        </PolicyParagraph>
        <PolicyParagraph>
          However, no method of transmission or storage is completely secure,
          and we cannot guarantee absolute security.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Children's Privacy">
        <PolicyParagraph>
          The Service is not intended for individuals under 13 years of age.
        </PolicyParagraph>
        <PolicyParagraph>
          We do not knowingly collect personal information from children under
          13.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Third-Party Services">
        <PolicyParagraph>
          The Service may integrate with third-party services. We are not
          responsible for the privacy practices of those third parties.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Changes to this Policy">
        <PolicyParagraph>
          We may update this Privacy Policy periodically.
        </PolicyParagraph>
        <PolicyParagraph>
          Material changes will be posted on the Service and reflected by the
          updated &ldquo;Last Updated&rdquo; date.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Contact Us">
        <PolicyParagraph>
          Questions about this Privacy Policy may be directed to:
        </PolicyParagraph>
        <p className="type-body-base text-text">Artisaire</p>
        <p className="type-body-base text-text-muted">
          Email:{" "}
          <a
            href="mailto:info@artisaire.com"
            className="text-brand underline-offset-4 hover:underline"
          >
            info@artisaire.com
          </a>
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
